import { useAccount, useImpersonateUser } from "@qw/auth";
import { Roles, useImpersonatableAccountsQuery, useInboxesQuery } from "@qw/graphql";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  Icon,
  useToast,
} from "@qw/ui";
import { type FC, useMemo } from "react";
import { SUBMISSION_EMAIL, SUBMISSIONS_EMAIL_NAME } from "@/constants/inbox";
import { IMPERSONATORS } from "../utils/impersonation";

export const ImpersonationMenu: FC = () => {
  const [impersonateUser] = useImpersonateUser();
  const { user } = useAccount();
  const { toast } = useToast();
  // ImpersonatableAccounts returns every internal non-Admin role (not just Broker), so a "pure"
  // UnderwritingAssistant/ARM/etc. is now a candidate here too. QWIB additionally requires the target
  // to actually own an inbox, so we intersect the result with the inbox list below.
  const { data: accountsData, loading: accountsLoading } = useImpersonatableAccountsQuery({
    fetchPolicy: "cache-first",
  });
  const { data: inboxData, loading: inboxLoading } = useInboxesQuery();

  const filteredInboxAccounts = useMemo(() => {
    const inboxes = inboxData?.inboxes?.map((i) => i.email) || [];
    const inboxAccounts = accountsData?.impersonatableAccounts || [];

    // Admins can impersonate any internal user who owns an inbox. The query already scopes to internal
    // non-Admin roles; the Admin re-check guards the Admin+Broker case the role filter still returns.
    if (user.roles.includes(Roles.Admin)) {
      return inboxAccounts.filter(
        (inboxAccount) => !inboxAccount.roles.includes(Roles.Admin) && inboxes.includes(inboxAccount.email)
      );
    }

    // Non-admin users can only impersonate accounts that are in their impersonation whitelist
    const allowedTargets = IMPERSONATORS[user.email];

    if (!allowedTargets) {
      return [];
    }

    return inboxAccounts.filter(({ email }) => allowedTargets.includes(email) && inboxes.includes(email));
  }, [accountsData, inboxData, user.email, user.roles]);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        disabled={accountsLoading || inboxLoading || !filteredInboxAccounts[0]}
        className="data-disabled:opacity-50"
      >
        <Icon icon="supervised_user_circle" />
        Impersonate User
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuItem
          key={SUBMISSIONS_EMAIL_NAME}
          onClick={() => {
            void impersonateUser({
              variables: { emailToImpersonate: SUBMISSION_EMAIL },
              onCompleted: () => globalThis.location.reload(),
              onError: (error) =>
                toast({ title: "Couldn't impersonate user", description: error.message, variant: "destructive" }),
            }).catch(() => {});
          }}
        >
          <Icon icon="account_circle" />
          {SUBMISSIONS_EMAIL_NAME}
        </DropdownMenuItem>

        {filteredInboxAccounts.map((u) => (
          <DropdownMenuItem
            key={u.id}
            onClick={() => {
              void impersonateUser({
                variables: { userIdToImpersonate: u.id },
                onCompleted: () => globalThis.location.reload(),
                onError: (error) =>
                  toast({ title: "Couldn't impersonate user", description: error.message, variant: "destructive" }),
              }).catch(() => {});
            }}
          >
            <Icon icon="account_circle" />
            {u.firstName} {u.lastName}
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
